import React, { useContext, useReducer } from 'react';
import axios from 'axios';
import AudienceContext from './AudienceContext';
import AudienceReducer from './AudienceReducer';
import MainContext from '../main/MainContext';
import { GET_AUDIENCE, REFRESH_AUDIENCE } from '../types';
import AuthContext from '../auth/AuthContext';
import { AUDTYPE } from '../../components/Utilities/NamingConversions';
import { isCurrentSynthDataset } from '../../components/Utilities/NumberConversions';

const defaultLoadingState = { main: 0 };

function AudienceState(props) {
  const authContext = useContext(AuthContext);
  const { loadingDecrement, loadingIncrement, handleError } =
    useContext(MainContext);

  const initialState = {
    audience: [],
    loading: defaultLoadingState,
    link: null,
    id: null,
    currAudience: null,
  };
  const [state, dispatch] = useReducer(AudienceReducer, initialState);

  const isValid = (audience) => {
    // invalid audiences are completely disabled (greyed out)
    // 'view only' audiences are all audiences that are valid, but not the
    // latest dataset.
    //
    // # for ca: 1 = sp2022q1, 2 = sp2023q1_ca, 3 = sp2023q2_ca
    // # for ca: 4 = sp2023q3,
    // # for us: 1 = sp2022q2_us, 2 = sp2023q3_us / sp2026q1_us
    return (
      (audience.country_code === 'ca' && audience.version === 4) ||
      (audience.country_code === 'us' && audience.version === 2)
    );
  };

  const isEditable = (audience) => {
    if (audience.ids_data?.dataset) {
      return isCurrentSynthDataset(
        audience.country_code,
        audience.ids_data.dataset
      );
    }
    return false;
  };

  const shareAudienceWithOrg = async ({ audience_id, sharedWithValue }) => {
    try {
      loadingIncrement(AUDTYPE);
      const company = authContext.userInfo?.company;
      const token = await authContext.getIdToken();
      await axios.post(
        '/share_audience_with_org',
        {
          company,
          audience_id: audience_id,
          share: sharedWithValue,
        },
        {
          headers: { Authorization: token },
        }
      );
      getAudiences();
    } catch (err) {
      // can handle forbidden action and general error
      handleError(err);
    } finally {
      loadingDecrement(AUDTYPE);
    }
  };

  // function which gets both private and shared reports.
  const getAudiences = async (country = authContext.userCountryCode) => {
    try {
      loadingIncrement(AUDTYPE);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_user_audiences',
        {
          countryCode: country,
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: GET_AUDIENCE,
        payload: {
          audience: res.data,
        },
      });
    } catch (err) {
      handleError(err);
      return err;
    } finally {
      loadingDecrement(AUDTYPE);
    }
  };

  // function which gets both private and shared reports.
  const saveAudience = async (audience) => {
    try {
      loadingIncrement(AUDTYPE);
      const token = await authContext.getIdToken();
      await axios.post(
        '/save_audience',
        {
          ...audience,
          countryCode: authContext.userCountryCode,
        },
        {
          headers: { Authorization: token },
        }
      );
      getAudiences();
    } catch (err) {
      handleError(err);
      return err;
    } finally {
      loadingDecrement(AUDTYPE);
    }
  };

  const setCurrAudience = (audience) => {
    dispatch({
      type: 'SET_CURR_AUDIENCE',
      payload: {
        currAudience: audience,
      },
    });
  };

  const updateAudience = async (id, attributes) => {
    try {
      loadingIncrement(AUDTYPE);
      const token = await authContext.getIdToken();
      await axios.post(
        '/update_audience',
        {
          id: id,
          attributes: attributes,
        },
        {
          headers: { Authorization: token },
        }
      );
      getAudiences();
    } catch (error) {
      handleError(error);
    } finally {
      loadingDecrement(AUDTYPE);
    }
  };

  const deleteAudience = async (deleteKey) => {
    try {
      loadingIncrement(AUDTYPE);
      const token = await authContext.getIdToken();
      await axios.post(
        '/delete_audience',
        {
          audienceId: deleteKey,
        },
        {
          headers: { Authorization: token },
        }
      );
      getAudiences();
    } catch (error) {
      console.error(`Failed to delete audience: ${error.message}`);
      handleError(error);
    } finally {
      loadingDecrement(AUDTYPE);
    }
  };
  const refreshAudience = async () => {
    dispatch({
      type: REFRESH_AUDIENCE,
      payload: {
        audience: [],
        currAudience: null,
      }
    });
  }

  return (
    <AudienceContext.Provider
      value={{
        audience: state.audience,
        sharedAudience: state.sharedAudience,
        currAudience: state.currAudience,
        isValid,
        isEditable,
        shareAudienceWithOrg,
        getAudiences,
        setCurrAudience,
        updateAudience,
        saveAudience,
        deleteAudience,
        refreshAudience
      }}
    >
      {props.children}
    </AudienceContext.Provider>
  );
}

export default AudienceState;
